import { PlusIcon } from '@heroicons/react/24/outline';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { Users } from 'lucide-react';
import type React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { useShowCreateTaskDialog } from '../../tasks/hooks/use-show-create-task-dialog.ts';
import { TeamFilters } from './team-filters.tsx';
import { TeamStats } from './team-stats.tsx';
import { UserOverviewTable } from './user-overview-table.tsx';

/**
 * The manager-only Team page (`/team`): the manager cockpit's per-rep table
 * with shared filters and stat cards. Reachable by a manager in Manager view
 * who can view the team page.
 */
export function TeamPage(): React.JSX.Element {
    const { canViewTeamPage, enableManagerView } = useAppConfig();
    const { t } = useTranslation('team');
    const sendEvent = useAnalytics();
    const showCreateTaskDialog = useShowCreateTaskDialog();

    const pageAvailable = canViewTeamPage && enableManagerView;

    useEffect(() => {
        if (pageAvailable) {
            sendEvent('team_page_viewed');
        }
    }, [pageAvailable, sendEvent]);

    if (!pageAvailable) {
        return <Navigate to="/" replace />;
    }

    return (
        <Page>
            <PageHeader
                icon={Users}
                title={t('page_title')}
                subTitle={t('page_sub_title')}
                actions={
                    <Button
                        variant="primary"
                        icon={PlusIcon}
                        onClick={() => {
                            showCreateTaskDialog({ source: 'team_header' });
                        }}
                    >
                        {t('top_navbar__new_task', { ns: 'common' })}
                    </Button>
                }
            />
            <TeamFilters />
            <TeamStats />
            <div className="flex flex-col sm:min-h-0 sm:flex-1 sm:overflow-y-auto">
                <UserOverviewTable taskSource="team_user" />
            </div>
        </Page>
    );
}
